import { useState } from 'react';
import {
  AlertTriangle,
  Ban,
  Calendar,
  LucideIcon,
  MapPin,
  Package,
  Repeat,
  ShieldCheck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { useToggleCarrierActive } from '@/hooksApi';
import { CarrierIndexSettingItem } from '@/types';
import { RestrictedItemsModal } from '@/components/Carriers/RestrictedItemsModal';
import { getTemperatureCoverage, listAllProvinceNames } from '@/utils/temperatureCoverage';
import { CarrierAvatar } from './CarrierAvatar';
import { CarrierFeatureChip } from './CarrierFeatureChip';
import { TemperatureCoverageChip } from './TemperatureCoverageChip';

interface Feature {
  key: string;
  icon: LucideIcon;
}

/**
 * Carriers whose service is confined to intra-Baghdad delivery. This is a
 * geographic RESTRICTION, not a capability — it is chipped alongside the
 * capabilities for scanability, but excluded from the "N features" count.
 */
const BAGHDAD_ONLY_SLUGS = ['nisr', 'sandoog'];

const isTrue = (value?: string | boolean | null) => value === true || value === 'true';

/**
 * same_day_delivery is a cut-off time ("20:30") when offered. The API OMITS the
 * key for carriers with no same-day config, so a `!== null` test was true for
 * `undefined` and advertised same-day on 29 of 34 carriers. Require a value.
 */
const hasSameDayCutoff = (value?: string | null) =>
  typeof value === 'string' && value.trim() !== '';

/**
 * Capability chips, in the order the design renders them. Temperature control
 * is not here — it is interactive (province popover) and rendered separately.
 */
function getFeatures(item: CarrierIndexSettingItem): Feature[] {
  const attributes = item.attributes;
  if (!attributes) return [];

  const features: Feature[] = [];

  if (hasSameDayCutoff(attributes.same_day_delivery))
    features.push({ key: 'same_day', icon: Calendar });
  if (isTrue(attributes.fragility_handling)) features.push({ key: 'fragile', icon: Package });
  if (isTrue(attributes.clue_link_verification))
    features.push({ key: 'clue_verification', icon: ShieldCheck });

  // A carrier that accepts RETURN and/or EXCHANGE order types can carry a
  // reverse shipment. DEFAULT alone means forward-only.
  const orderTypes = attributes.order_type ?? [];
  if (orderTypes.some((type) => type === 'RETURN' || type === 'EXCHANGE'))
    features.push({ key: 'return_exchange', icon: Repeat });

  return features;
}

interface CarrierCardProps {
  item: CarrierIndexSettingItem;
  disabled?: boolean;
  provinceRows?: Array<{
    province_code?: string | null;
    province_en_name?: string | null;
    province?: string | null;
  }>;
}

export function CarrierCard({ item, disabled = false, provinceRows = [] }: CarrierCardProps) {
  const { t, i18n } = useTranslation();
  const { mutate: toggleActive } = useToggleCarrierActive();
  const [isToggling, setIsToggling] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'enable' | 'view'>('view');

  const enabled = item.is_active_for_merchant;
  const features = getFeatures(item);
  const logo = item.avatar?.abs_url;
  const locale = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const coverage = getTemperatureCoverage(item.attributes, provinceRows, locale);

  // An unrestricted cold-chain carrier serves every province the platform
  // knows about — name them rather than saying a vague "all provinces".
  const coverageProvinces = coverage.show
    ? coverage.variant === 'all'
      ? listAllProvinceNames(provinceRows, locale)
      : coverage.provinceNames
    : [];

  const carrierDisplayName = t(item.slug, { defaultValue: item.title });
  const isBaghdadOnly = BAGHDAD_ONLY_SLUGS.includes(item.slug);

  // Capabilities only. The Baghdad-only restriction is not a feature, and
  // restricted items are a warning rather than something the carrier offers.
  const featureCount = features.length + (coverage.show ? 1 : 0);
  const hasChips = featureCount > 0 || isBaghdadOnly;

  const doToggle = () => {
    setIsToggling(true);
    toggleActive(item.uid, {
      onSettled: () => setIsToggling(false),
    });
  };

  const handleToggle = () => {
    // Item 1 — Lock Shipping Rules. The Switch is already `disabled` when
    // locked; guard the handler too so a stale render can never fire a
    // toggle the backend would 403.
    if (disabled) return;
    // When toggling ON and carrier has restricted items — show modal first
    if (!enabled && item.has_restricted_items) {
      setModalMode('enable');
      setModalOpen(true);
      return;
    }
    doToggle();
  };

  const handleViewRestrictedItems = () => {
    setModalMode('view');
    setModalOpen(true);
  };

  return (
    <>
      <div className='flex flex-col rounded-[14px] border border-sd-border bg-sd-card p-5 transition-shadow hover:shadow-md'>
        {/* Header: logo + name/status + toggle */}
        <div className='flex items-center gap-3'>
          <CarrierAvatar
            src={logo}
            name={carrierDisplayName}
            slug={item.slug}
            size={44}
            className='shrink-0'
          />
          <div className='flex min-w-0 flex-1 flex-col'>
            <span className='text-base font-bold'>{carrierDisplayName}</span>
            {/* Pluralize via i18next, not a bolted-on 's'. `plural_suffix` was
                "s" in BOTH locales, so Arabic rendered "2 خاصيةs". Arabic has
                six plural forms; Intl.PluralRules picks the right one. */}
            <span className='whitespace-nowrap text-[13px] text-sd-muted-foreground'>
              {enabled ? t('active') : t('inactive')} · {featureCount}{' '}
              {t('feature', { count: featureCount })}
            </span>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={disabled || isToggling}
            className='data-[state=checked]:bg-sd-primary'
          />
        </div>

        {/* Capability chips — one uniform style, no chip shouts louder than another */}
        {hasChips && (
          <>
            <div className='my-4 h-px bg-sd-border' />
            <div className='flex flex-wrap gap-2'>
              {features.map((feature) => (
                <CarrierFeatureChip
                  key={feature.key}
                  icon={feature.icon}
                  label={String(t(feature.key))}
                />
              ))}

              {isBaghdadOnly && (
                <CarrierFeatureChip icon={MapPin} label={String(t('baghdad_to_baghdad_only'))} />
              )}

              {coverage.show && (
                <TemperatureCoverageChip
                  label={String(t('temperature_control_provides'))}
                  popoverTitle={String(t('temp_controlled_in'))}
                  provinceNames={coverageProvinces}
                />
              )}
            </div>
          </>
        )}

        {/* Restricted items — the one deliberately loud element on the card */}
        {item.has_restricted_items && (
          <>
            <div className='my-4 h-px bg-sd-border' />
            <button
              onClick={handleViewRestrictedItems}
              className='flex cursor-pointer items-center gap-[7px] border-0 bg-transparent p-0 text-sm font-medium text-amber-600 hover:text-amber-700 hover:underline'
            >
              <AlertTriangle className='h-[15px] w-[15px] shrink-0' strokeWidth={2} />
              {String(t('restricted_items'))}
            </button>
          </>
        )}

        {/* Nothing to show at all */}
        {!hasChips && !item.has_restricted_items && (
          <>
            <div className='my-4 h-px bg-sd-border' />
            <div className='flex items-center gap-2 text-[13px] font-medium text-sd-muted-foreground'>
              <Ban className='h-[15px] w-[15px] shrink-0' strokeWidth={2} />
              {String(t('no_features_enabled'))}
            </div>
          </>
        )}
      </div>

      <RestrictedItemsModal
        carrierName={carrierDisplayName}
        carrierSlug={item.slug}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        onConfirmEnable={doToggle}
      />
    </>
  );
}
